import { useTranslate } from '@tolgee/react'
import { TomTable, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { TomTableToolbarAlertBanner } from 'modules/ui/src/theme/components/table/organisms/TomTableToolbarAlertBanner'
import { useMemo } from 'react'
import {
  canStartWorkInstruction,
  canStopWorkInstruction,
} from '../../helpers/work-instruction-table.helper'
import { useWorkInstructionTableActions } from '../../hooks/useWorkInstructionTableActions'
import { WorkInstructionViewModel } from '../../models/work-instruction-view.model'
import { WorkInstructionUIStore } from '../../stores/work-instruction.ui-store'
import { getWorkInstructionColumns } from '../../tableConfigs/workInstructionColumns'
import { WorkInstructionMenuOptions } from '../atoms/WorkInstructionMenuOptions'
import { WorkInstructionJobs } from '../molecules/WorkInstructionJobs'

interface Props {
  store: WorkInstructionUIStore
}
export const WorkInstructionTable = observer(({ store }: Props) => {
  const { t } = useTranslate()

  const getTableActions = useWorkInstructionTableActions(store)

  const columns = useMemo(
    () => getWorkInstructionColumns(t, store, store.selectedTabType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, store, store.selectedTabType],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'operations/work-instructions',
  )

  const table = useTomTable<WorkInstructionViewModel>(
    {
      data: store.filteredWorkInstructions,
      columns: columns,
      enableRowSelection: store.hasTableActions
        ? row =>
            canStopWorkInstruction(row.original.status) ||
            canStartWorkInstruction(row.original.status)
        : false,
      enableRowActions: store.hasTableActions,

      renderRowActions: ({ row }) => (
        <WorkInstructionMenuOptions
          type='controlRoom'
          workInstruction={row.original}
          uiStore={store}
        />
      ),
      getRowId: row => row.id.toString(),
      getRowCanExpand: row => row.original.jobs.length > 0,
      renderDetailPanel: ({ row }) =>
        row.original.jobs.length ? <WorkInstructionJobs jobs={row.original.jobs} /> : null,

      renderToolbarAlertBannerContent: ({ table }) => {
        const selectedRows = table.getSelectedRowModel().rows
        const dynamicActions = getTableActions(selectedRows, store.canStartOrStop)

        if (!dynamicActions.length) {
          return null
        }

        return <TomTableToolbarAlertBanner table={table} tableActions={dynamicActions} />
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
